<template>
    <select v-show="canShow"
            :form="form"
            :name="name"
            :required="required"
    >
    </select>
</template>

<script>
import _ from 'lodash';

export default {
    name: "select2",
    emits: ['input', 'update:value'],
    props: {
        options: {
            type: Object,
            default: () => {
                return null
            }
        },
        ajaxOptions: {
            type: Object,
            default: () => {
                return null
            }
        },
        // Pass an array of the indices you would like to be selected by default.
        value: {
            type: [Array, Number, Object, String],
            default: null,
        },
        name: String,
        allowClear: {type: Boolean, default: false},
        // Tags creates new options from text input by the user
        tags: {type: Boolean, default: false},    // https://select2.org/tagging
        // Multiple set to true allows users to select multiple items
        multiple: {type: Boolean, default: false},
        // Placeholder
        placeholder: {type: String, default: ''},
        // Close ON Select
        closeOnSelect: {type: Boolean, default: false},
        // Width (percent is appended)
        width: {
            type: String,
            default: '100',
        },
        // Modify the input to search for alphanumeric only?
        // This is helpful for things like serial numbers
        matchAlphaNumeric: {
            type: Boolean,
            default: false,
        },
        form: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        canSelectAll: {
            type: Boolean,
            default: false,
        },
        sortByText: {
            type: Boolean,
            default: false,
        },
        disableSearch: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            _options: {},
            sanitizedOptions: [],
            recentUpdatedValue: false,
            updateTimeInterval: 100,
            element: null,
            select2Built: false,
            useAjax: true,
        }
    },
    mounted() {
        if (_.isNil(this.$data._options)) {
            return
        }

        this.buildSelect2()
    },
    methods: {
        buildSelect2() {
            if (this.select2Built) {
                return
            }

            this.$data._options = _.clone(this.options)
            if (this.isSelectAllSetup) {
                this.$data._options['all'] = 'Select All'
            }

            this.convertPluckToSelect2(this.$data._options);

            let select2Options = {
                data: this.sanitizedOptions,
                tags: this.tags,
                name: this.name,
                multiple: this.multiple ? 'multiple' : '',
                placeholder: this.placeholder,
                width: this.width + '%',
                containerCssClass: '',
                allowClear: this.allowClear,
                closeOnSelect: this.closeOnSelect,
                disabled: this.disabled,
            };

            if (this.disableSearch) {
                select2Options.minimumResultsForSearch = Infinity;
            }

            if (this.matchAlphaNumeric) {
                select2Options.matcher = this.matcherAlphaNum;
            }

            this.element = $(this.$el)
                // init select2
                .select2(select2Options)

            // emit event on change.
            $(this.element).val(this.value)
            $(this.element)
                .on("change", () => {
                    this.$emit("input", $(this.element).val())
                    this.$emit("update:value", $(this.element).val())
                    // $(this.element).val(null);
                })
                .trigger("change")

            if (this.multiple) {
                // select2 4.0 toggles the dropdown open right after the allow-clear "×" runs. Clearing a
                // multiple select removes its chips, so the box shrinks and the freshly opened results
                // list slides up under the cursor — the same click's mouseup then re-selects the first
                // option. Swallow the one "opening" that immediately follows a clear.
                $(this.element).on("select2:clear", () => {
                    const suppressOpen = (event) => event.preventDefault()
                    $(this.element).one("select2:opening", suppressOpen)
                    // Safety net: if no opening followed synchronously, don't leave the handler armed to
                    // swallow the user's next deliberate open.
                    setTimeout(() => $(this.element).off("select2:opening", suppressOpen), 0)
                })
            }
            this.select2Built = true
        },
        convertPluckToSelect2(input) {
            if (!input) {
                this.sanitizedOptions = []
                return
            }
            let newOptions = [];
            for (let [key, value] of Object.entries(input)) {

                let selected = false
                if (this.value instanceof Array) {
                    selected = this.value.includes(key) || this.value.includes(Number.parseInt(key))

                } else if (this.value instanceof Object) {
                    selected = _.has(this.value, key)
                } else {
                    selected = this.value == key;
                }

                let thisOption = {
                    id: key,
                    text: value,
                    selected: selected,
                };
                newOptions.push(thisOption);
            }

            if (this.sortByText) {
                newOptions.sort((a, b) => a.text.localeCompare(b.text));
            }

            this.sanitizedOptions = newOptions;
        },
        matcherAlphaNum(params, data) {
            if (params !== undefined && params.term !== undefined) {
                params.term = params.term.replace(/[^0-9a-z]/gi, '');
            }
            let defaultMatcher = $.fn.select2.defaults.defaults.matcher;
            return defaultMatcher(params, data);
        },
        isValueSelected(value) {
            if (this.value === null || this.value === [] || this.value === {}) {
                return false
            } else if (this.value instanceof String || this.value instanceof Number) {
                return value == this.value
            } else if (this.value instanceof Object) {
                return Object.keys(this.value).indexOf(value) !== -1
            } else if (this.value instanceof Array) {
                return this.value.indexOf(value) !== -1
            }
        },
        close() {
            $(this.element).select2("close");
        },
        destroySelect2() {
            if (this.select2Built) {
                $(this.element).select2("destroy");
                this.select2Built = false
            }
        },
    },
    computed: {
        canShow() {
            return this.select2Built
        },
        isSelectAllSetup() {
            return this.canSelectAll && this.multiple
        },
    },
    watch: {
        value: {
            handler(value, oldVal) {
                if (value === oldVal || this.recentUpdatedValue) {
                    return
                }

                let allIndex = _.isArray(value) ? value.indexOf('all') : -1
                console.log(`[Select2.${this.name}]: allIndex: ${allIndex}`)
                if (this.isSelectAllSetup && allIndex !== -1) {
                    console.log(`[Select2.${this.name}]: Changing w/  select All`)
                    let newSelections = Object.keys(this.$data._options)
                    console.log(`[Select2.${this.name}]: New Selections: ${newSelections}`)

                    let selectionAllIndex = newSelections.indexOf("all")
                    console.log(`[Select2.${this.name}]: selectionAllIndex: ${selectionAllIndex}`)

                    newSelections.splice(selectionAllIndex, 1)
                    console.log(`[Select2.${this.name}]: Selections w/out 'all': ${newSelections}`)

                    value = newSelections
                }

                $(this.element)
                        .val(value)
                        .trigger("change");

                // Delay the next time that value can be updated.
                this.recentUpdatedValue = true
                setTimeout(() => {
                    this.recentUpdatedValue = false
                }, 100)
            },
            deep: true
        },
        options: {
            handler(newValue, oldValue) {
                if (this.select2Built) {
                    let selectChildren = $(this.element).children()
                    for (let count = 0; count < selectChildren.length; count++) {
                        selectChildren[count].remove()
                    }
                    this.destroySelect2()
                }
                this.buildSelect2()
            },
            deep: true
        },
        disabled: {
            handler(value) {
                if (this.select2Built) {
                    $(this.element).prop("disabled", value).trigger("change.select2")
                }
            },
        }
    },
    unmounted() {
        this.destroySelect2()
    },
}
</script>

<style>
.select2-container--default .select2-selection--single {
    border-radius: 6px;
    border: 1px solid rgb(212 212 216);
}

.select2-container--default .select2-selection--multiple {
    border: 1px solid rgb(212 212 216);
    border-radius: 6px;
}

.select2-dropdown {
    border: 1px solid rgb(212 212 216);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    border-radius: 6px;
}

.select2-container--default .select2-search--dropdown .select2-search__field {
    border: 1px solid rgb(212 212 216);
    border-radius: 6px;
}

.select2-container {
    margin-top: 0 !important;
}

.select2-container .select2-selection--single {
    height: 32px;
}

.select2-container--default .select2-selection--single .select2-selection__rendered {
    line-height: 32px;
}

.select2-container--default .select2-selection--single .select2-selection__arrow {
    height: 30px;
}

.select2-container--disabled .select2-selection--single {
    background-color: rgb(243 244 246);
    cursor: not-allowed;
}

.select2-container--disabled .select2-selection--single .select2-selection__rendered {
    color: rgb(156 163 175);
}
</style>